/**
 * Canonical JSON and SHA-256 without platform dependencies. Used for audit digests, content bundle
 * digests, export manifests and stable-key generation. Pure TypeScript so the same code runs in
 * the worker, main process, server and (if ever needed) the sandboxed renderer.
 */

/**
 * Deterministic JSON: object keys sorted lexicographically at every level, arrays in order,
 * `undefined` properties omitted, no whitespace. Throws on values JSON cannot represent
 * (functions, symbols, bigint, cycles) and on non-finite numbers so digests never silently differ.
 */
export function canonicalJson(value: unknown): string {
  const seen = new Set<object>();
  const walk = (v: unknown): string => {
    if (v === null) return 'null';
    switch (typeof v) {
      case 'string':
        return JSON.stringify(v);
      case 'number':
        if (!Number.isFinite(v)) throw new TypeError('canonicalJson: non-finite number');
        return Number.isInteger(v) && Math.abs(v) < 1e21 ? String(v) : JSON.stringify(v);
      case 'boolean':
        return v ? 'true' : 'false';
      case 'bigint':
        throw new TypeError('canonicalJson: bigint is not representable; convert to string first');
      case 'undefined':
      case 'function':
      case 'symbol':
        throw new TypeError(`canonicalJson: ${typeof v} is not representable`);
      case 'object': {
        const obj = v as object;
        if (seen.has(obj)) throw new TypeError('canonicalJson: cyclic structure');
        seen.add(obj);
        let out: string;
        if (Array.isArray(obj)) {
          out = `[${obj.map((item) => (item === undefined ? 'null' : walk(item))).join(',')}]`;
        } else if (obj instanceof Date) {
          out = JSON.stringify(obj.toISOString());
        } else {
          const record = obj as Record<string, unknown>;
          const keys = Object.keys(record)
            .filter((k) => record[k] !== undefined)
            .sort();
          out = `{${keys.map((k) => `${JSON.stringify(k)}:${walk(record[k])}`).join(',')}}`;
        }
        seen.delete(obj);
        return out;
      }
      default:
        throw new TypeError('canonicalJson: unsupported value');
    }
  };
  return walk(value);
}

// ---------------------------------------------------------------------------------------------
// SHA-256 (FIPS 180-4), synchronous, pure TypeScript
// ---------------------------------------------------------------------------------------------

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98,
  0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8,
  0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819,
  0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
  0xc67178f2,
]);

function utf8Bytes(input: string): Uint8Array {
  return new TextEncoder().encode(input);
}

/** SHA-256 digest as lowercase hex. Accepts a UTF-8 string or raw bytes. */
export function sha256Hex(input: string | Uint8Array): string {
  const data = typeof input === 'string' ? utf8Bytes(input) : input;
  const bitLength = data.length * 8;
  const paddedLength = (((data.length + 9 + 63) / 64) | 0) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(data);
  padded[data.length] = 0x80;
  const view = new DataView(padded.buffer);
  // 64-bit big-endian length; JS numbers are exact for lengths < 2^53 bits.
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000), false);
  view.setUint32(paddedLength - 4, bitLength >>> 0, false);

  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);
  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4, false);
    for (let i = 16; i < 64; i++) {
      const w15 = w[i - 15]!;
      const w2 = w[i - 2]!;
      const s0 = ((w15 >>> 7) | (w15 << 25)) ^ ((w15 >>> 18) | (w15 << 14)) ^ (w15 >>> 3);
      const s1 = ((w2 >>> 17) | (w2 << 15)) ^ ((w2 >>> 19) | (w2 << 13)) ^ (w2 >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }
    let a = h[0]!;
    let b = h[1]!;
    let c = h[2]!;
    let d = h[3]!;
    let e = h[4]!;
    let f = h[5]!;
    let g = h[6]!;
    let hh = h[7]!;
    for (let i = 0; i < 64; i++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K[i]! + w[i]!) >>> 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0]! + a) >>> 0;
    h[1] = (h[1]! + b) >>> 0;
    h[2] = (h[2]! + c) >>> 0;
    h[3] = (h[3]! + d) >>> 0;
    h[4] = (h[4]! + e) >>> 0;
    h[5] = (h[5]! + f) >>> 0;
    h[6] = (h[6]! + g) >>> 0;
    h[7] = (h[7]! + hh) >>> 0;
  }
  let hex = '';
  for (let i = 0; i < 8; i++) hex += h[i]!.toString(16).padStart(8, '0');
  return hex;
}

/** sha256 over the canonical JSON of a value. */
export function digestJson(value: unknown): string {
  return sha256Hex(canonicalJson(value));
}
